import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, FileSpreadsheet, Eye, History, Loader2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTimesheetMutations } from '@/hooks/timesheets';
import { useToast } from '@/hooks/use-toast';
import { usePayrollEmployees, usePayrollPreview, usePayrollMutations } from '@/hooks/payroll';
import { usePayrollExport } from '@/hooks/payroll/usePayrollExport';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions';
import { usePayrollRunsRealtimeSubscription } from '@/hooks/payroll';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import { companyTimezoneHint } from '@/lib/timesheetTime';
import {
  EmployeeSelector, 
  SelectedEmployeeSummary, 
  PayrollPreviewDialog,
  PayrollRunsTable,
  PayrollRunDetails
} from '@/components/payroll';
import type { PayrollRunSummary } from '@/services/payroll/types';
import type { PayrollRunWithDetails } from '@/services/payroll/payrollHistoryService';

const PayrollManagement = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Page-scoped realtime subscription for payroll updates
  usePayrollRunsRealtimeSubscription();
  
  // Set default dates to previous month
  const previousMonth = subMonths(new Date(), 1);
  const [payrollStartDate, setPayrollStartDate] = useState(
    format(startOfMonth(previousMonth), 'yyyy-MM-dd')
  );
  const [payrollEndDate, setPayrollEndDate] = useState(
    format(endOfMonth(previousMonth), 'yyyy-MM-dd')
  );
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('generate');
  // Guards the entire createRun → generatePayroll sequence against double-submit.
  // generatePayroll.isPending only becomes true after createRun completes, leaving a window
  // where a second click could fire a second createRun before isPending is set.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Salary-only / leave-only employees are opt-in so the default list stays
  // "people with activity this period".
  const [includeNonWorking, setIncludeNonWorking] = useState(false);
  
  // Permission check for exporting timesheets
  const canExportTimesheets = useHasPermission('export_timesheets');
  
  const { exportPayrollData } = useTimesheetMutations();
  const { createRun, generatePayroll } = usePayrollMutations();
  const previewMutation = usePayrollPreview();
  const { exportRun } = usePayrollExport();
  const { profile } = useAuth();
  const companyTimezone = useCompanyTimezone();

  const {
    data: employees = [],
    isLoading: isLoadingEmployees,
    error: employeesError,
    refetch: refetchEmployees,
  } = usePayrollEmployees(payrollStartDate, payrollEndDate, includeNonWorking);

  // Prune (never silently keep) selected IDs that are absent from the current
  // result, so run metadata can't carry employees the operator never saw.
  useEffect(() => {
    if (isLoadingEmployees) return;
    setSelectedEmployeeIds((prev) => {
      const present = prev.filter(id => employees.some(emp => emp.profile_id === id));
      return present.length === prev.length ? prev : present;
    });
  }, [employees, isLoadingEmployees]);

  const selectedEmployees = useMemo(() => {
    return employees.filter(emp => selectedEmployeeIds.includes(emp.profile_id));
  }, [employees, selectedEmployeeIds]);

  const payrollSummary: PayrollRunSummary = useMemo(() => {
    return {
      selectedCount: selectedEmployees.length,
      totalHours: selectedEmployees.reduce((sum, emp) => sum + emp.total_hours, 0),
      employeeIds: selectedEmployeeIds,
    };
  }, [selectedEmployees, selectedEmployeeIds]);

  const validateDates = (): boolean => {
    if (!payrollStartDate || !payrollEndDate) {
      toast({
        title: "Validation Error",
        description: "Please select both start and end dates",
        variant: "destructive",
      });
      return false;
    }

    if (new Date(payrollStartDate) > new Date(payrollEndDate)) {
      toast({
        title: "Invalid Date Range",
        description: "Start date cannot be after end date. Please correct the dates.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handlePreviewPayroll = () => {
    if (!validateDates()) {
      return;
    }

    if (selectedEmployeeIds.length === 0) {
      toast({
        title: "No Employees Selected",
        description: "Please select at least one employee to preview payroll",
        variant: "destructive",
      });
      return;
    }

    previewMutation.mutate(
      {
        startDate: payrollStartDate,
        endDate: payrollEndDate,
        selectedEmployees,
      },
      {
        onSuccess: () => {
          setShowPreview(true);
        },
        onError: (error) => {
          toast({
            title: "Preview Failed",
            description: error.message || "Failed to generate preview",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleConfirmGenerate = async () => {
    // Guard against double-submit: covers the entire createRun → generatePayroll sequence.
    // generatePayroll.isPending is only true after createRun completes, leaving a window
    // where a second click could create a second payroll run before isPending is set.
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Step 1: Create payroll run record
      const run = await createRun.mutateAsync({
        startDate: payrollStartDate,
        endDate: payrollEndDate,
        selectedEmployeeIds,
      });

      // Step 2: Generate payslips with run tracking
      await generatePayroll.mutateAsync({
        payrollRunId: run.id,
        startDate: payrollStartDate,
        endDate: payrollEndDate,
        selectedEmployeeIds,
      });

      // Success - clean up
      setShowPreview(false);
      setPayrollStartDate('');
      setPayrollEndDate('');
      setSelectedEmployeeIds([]);
      previewMutation.reset();
    } catch (error) {
      console.error('Payroll generation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportPayroll = () => {
    if (!validateDates()) {
      return;
    }

    exportPayrollData.mutate({ startDate: payrollStartDate, endDate: payrollEndDate });
  };

  const handleViewRunDetails = (run: PayrollRunWithDetails) => {
    setSelectedRunId(run.id);
  };

  const handleHistoryExport = canExportTimesheets
    ? (runId: string, format: 'csv' | 'json') => {
        exportRun.mutate({ runId, format });
      }
    : undefined;

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Payroll Management</h1>
        <p className="text-muted-foreground">
          Generate payslips and export payroll data for approved time entries
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Generate Payroll
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Payroll History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Pay Period</CardTitle>
              <CardDescription>
                Choose the date range for payroll processing. {companyTimezoneHint(companyTimezone)}.
              </CardDescription>
            </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={payrollStartDate}
                onChange={(e) => setPayrollStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={payrollEndDate}
                onChange={(e) => setPayrollEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div>
              <Label htmlFor="include-non-working" className="text-sm font-medium">
                Include salary / leave-only employees
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Also list employees with no approved time entries who have an active pay rule or approved paid leave in this period.
              </p>
            </div>
            <Switch
              id="include-non-working"
              checked={includeNonWorking}
              onCheckedChange={setIncludeNonWorking}
            />
          </div>
            </CardContent>
          </Card>

          {payrollStartDate && payrollEndDate && (
        <>
          {employeesError ? (
            <Card>
              <CardContent className="py-12 space-y-4 text-center">
                <p className="text-destructive font-medium">
                  Couldn't load the employee list for this period.
                </p>
                <p className="text-sm text-muted-foreground">
                  {(employeesError as Error).message}
                </p>
                <Button variant="outline" onClick={() => refetchEmployees()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : isLoadingEmployees ? (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-muted-foreground">Loading employees...</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <EmployeeSelector
                employees={employees}
                selectedIds={selectedEmployeeIds}
                onSelectionChange={setSelectedEmployeeIds}
              />

              {selectedEmployeeIds.length > 0 && (
                <>
                  <SelectedEmployeeSummary summary={payrollSummary} />

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <Button
                          onClick={handlePreviewPayroll}
                          disabled={previewMutation.isPending}
                          className="flex-1"
                        >
                          <span className="flex items-center gap-2">
                            {previewMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                            <span>
                              {previewMutation.isPending 
                                ? 'Loading Preview...' 
                                : `Preview Payroll (${selectedEmployeeIds.length})`
                              }
                            </span>
                          </span>
                        </Button>
                        {canExportTimesheets && (
                          <Button
                            variant="outline"
                            onClick={handleExportPayroll}
                            disabled={exportPayrollData.isPending}
                            className="flex-1"
                          >
                            <span className="flex items-center gap-2">
                              {exportPayrollData.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileSpreadsheet className="h-4 w-4" />
                              )}
                              <span>
                                {exportPayrollData.isPending ? 'Exporting...' : 'Export CSV'}
                              </span>
                            </span>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
            </>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Payroll History
              </CardTitle>
              <CardDescription>
                View past payroll runs and their details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PayrollRunsTable
                onViewDetails={handleViewRunDetails}
                onExport={handleHistoryExport}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PayrollPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        previewData={previewMutation.data || null}
        isLoading={previewMutation.isPending}
        onConfirm={handleConfirmGenerate}
        isGenerating={isSubmitting || generatePayroll.isPending}
      />

      <PayrollRunDetails
        runId={selectedRunId}
        open={!!selectedRunId}
        onOpenChange={(open) => !open && setSelectedRunId(null)}
      />
    </div>
  );
};

export default PayrollManagement;
